import { toNumber } from 'ezmoney';

import { type CurrencyOption } from 'src/core/config/money';

import {
  analyticalSplitFormValueToItems,
  type AnalyticalSplitItem,
} from '../analyticalSplit';
import { type CustomFieldAssociation } from '../customFieldAssociation';
import { type DraftTrait } from './budgetTrackingTrait';

// Compute the approvingCostCenterId as the cost center with the highest total amount across all split items
const getHighestStakeCostCenterId = (
  analyticalSplits: AnalyticalSplitItem[],
): string | null => {
  return Object.entries(
    analyticalSplits.reduce<Record<string, number>>((accumulator, item) => {
      if (item.costCenterId) {
        accumulator[item.costCenterId] =
          (accumulator[item.costCenterId] || 0) + toNumber(item.amount);
      }
      return accumulator;
    }, {}),
  ).reduce<{ costCenterId: string | null; totalAmount: number }>(
    (max, [costCenterId, totalAmount]) =>
      totalAmount > max.totalAmount ? { costCenterId, totalAmount } : max,
    { costCenterId: null, totalAmount: Number.NEGATIVE_INFINITY },
  ).costCenterId;
};

// this function builds budget tracking payload from form values
// especially, if analytical split is activated, it routes the custom fields associations
// between "root" and "split items", ensuring the correct mapping of data
// it aslo computes the cost center with highest amount in the split
// and allocates it as the "root" cost center
export const budgetTrackingFormValuesToPayload = (
  formValues: DraftTrait & {
    currency: CurrencyOption;
  },
): {
  teamId: string | null;
  costCenterId: string | null;
  customFieldsAssociations: CustomFieldAssociation[];
  analyticalSplits?: AnalyticalSplitItem[];
} => {
  const {
    costCenterId,
    teamId,
    customFieldsAssociations,
    analyticalSplit,
    currency,
  } = formValues;

  const analyticalSplits =
    analyticalSplitFormValueToItems({
      currency: currency.key,
      value: analyticalSplit,
    }) ?? [];

  const approvingCostCenterId =
    analyticalSplit?.activate && analyticalSplit?.dimensions.splitOnCostCenter
      ? getHighestStakeCostCenterId(analyticalSplits)
      : costCenterId;

  // if analytical split has exactly one item with 100% allocation
  // we merge data and send the form without split
  if (analyticalSplits.length === 1 && analyticalSplits[0].percentage === 100) {
    return {
      teamId: teamId ?? null,
      costCenterId: approvingCostCenterId ?? null,
      customFieldsAssociations: [
        ...(analyticalSplits[0].customFields ?? []),
        ...customFieldsAssociations.filter(
          (assoc) =>
            !analyticalSplit?.dimensions.splitOnCustomFieldsIds?.includes(
              assoc.customFieldId,
            ), // remove potential cf associations set before activating the split
        ),
      ],
      analyticalSplits: [],
    };
  }

  return {
    teamId: teamId ?? null,
    costCenterId: approvingCostCenterId ?? null,
    customFieldsAssociations: analyticalSplit?.activate
      ? customFieldsAssociations.filter(
          (assoc) =>
            !analyticalSplit.dimensions.splitOnCustomFieldsIds?.includes(
              assoc.customFieldId,
            ), // remove potential cf associations set before activating the split
        )
      : customFieldsAssociations,
    analyticalSplits,
  };
};
